import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { authAPI } from '../../api/auth';
import { shopAPI } from '../../api/shop';
import { programsAPI } from '../../api/programs';
import { eventsAPI } from '../../api/events';
import { 
  User, Mail, Phone, MapPin, Calendar, 
  Camera, Pencil, X,
  ShoppingBag, Package, UserPlus, UserCheck, Users, Flag, Target, Link2,
  Square, CheckSquare, ClipboardList, Trash2
} from 'lucide-react';
import toast from 'react-hot-toast';
import HealthFormModal from './HealthFormModal';
import RegistrationFormModal from './RegistrationFormModal';
import OrderDetailsModal from './OrderDetailsModal';
import AdminDashboardTab from './AdminDashboardTab';
import Button from '../shared/Button';
import ParticipantsModal from '../shared/ParticipantsModal';

const weekDaysOrder = ['Воскресенье', 'Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота'];
const weekDaysShort = {
  'Воскресенье': 'Вс',
  'Понедельник': 'Пн',
  'Вторник': 'Вт',
  'Среда': 'Ср',
  'Четверг': 'Чт',
  'Пятница': 'Пт',
  'Суббота': 'Сб',
};

const getTodayWeekdayRu = () => weekDaysOrder[new Date().getDay()];
const SOCIAL_HOST_RULES = {
  instagram: ['instagram.com'],
  facebook: ['facebook.com', 'fb.com'],
  twitter: ['twitter.com', 'x.com'],
  linkedin: ['linkedin.com'],
};

const GOALS_V2_PREFIX = 'GOALS_V2:';

const parseLegacyGoals = (rawGoals) => {
  const raw = String(rawGoals || '').trim();
  if (!raw) return [];

  const distanceMatch = raw.match(/Дистанция:\s*(.+)/i);
  const targetTimeMatch = raw.match(/Целевое время:\s*(.+)/i);
  const noteMatch = raw.match(/Комментарий:\s*(.+)/i);

  const distance = distanceMatch?.[1]?.trim() || '';
  const target_time = targetTimeMatch?.[1]?.trim() || '';
  const note = noteMatch?.[1]?.trim() || raw;
  const title = distance || note || 'Цель';

  if (!title && !target_time && !note) return [];

  return [
    {
      id: `legacy-${Date.now()}`,
      title,
      target_time,
      note,
      is_done: false,
      completion_journal: null,
      completed_at: null,
    },
  ];
};

const parseGoals = (rawGoals) => {
  const raw = String(rawGoals || '').trim();
  if (!raw) return [];

  if (!raw.startsWith(GOALS_V2_PREFIX)) {
    return parseLegacyGoals(raw);
  }

  try {
    const parsed = JSON.parse(raw.slice(GOALS_V2_PREFIX.length));
    if (!Array.isArray(parsed)) return [];

    return parsed
      .map((goal, index) => ({
        id: String(goal?.id || `goal-${index + 1}`),
        title: String(goal?.title || '').trim(),
        target_time: String(goal?.target_time || '').trim(),
        note: String(goal?.note || '').trim(),
        is_done: Boolean(goal?.is_done),
        completion_journal: goal?.completion_journal && typeof goal.completion_journal === 'object'
          ? {
            what_helped: String(goal.completion_journal.what_helped || '').trim(),
            hardest_part: String(goal.completion_journal.hardest_part || '').trim(),
            what_next: String(goal.completion_journal.what_next || '').trim(),
          }
          : null,
        completed_at: goal?.completed_at || null,
      }))
      .filter((goal) => goal.title || goal.target_time || goal.note);
  } catch (error) {
    console.error('Failed to parse goals payload:', error);
    return parseLegacyGoals(raw);
  }
};

const serializeGoals = (goals) => `${GOALS_V2_PREFIX}${JSON.stringify(goals)}`;

const UserDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [dashboardData, setDashboardData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showHealthForm, setShowHealthForm] = useState(false);
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [photoPreview, setPhotoPreview] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showAllOrdersModal, setShowAllOrdersModal] = useState(false);
  const [repeatOrderLoadingId, setRepeatOrderLoadingId] = useState(null);
  const [groupTrainings, setGroupTrainings] = useState([]);
  const [groupTrainingsLoading, setGroupTrainingsLoading] = useState(true);
  const [eventsForNotifications, setEventsForNotifications] = useState([]);
  const [userDocuments, setUserDocuments] = useState([]);
  const [groupTrainingSubscriptionLoadingId, setGroupTrainingSubscriptionLoadingId] = useState(null);
  const [participantsModalData, setParticipantsModalData] = useState({
    isOpen: false,
    title: '',
    participants: [],
    totalCount: 0,
  });
  const [selectedTrainingDay, setSelectedTrainingDay] = useState('');
  const [activeTab, setActiveTab] = useState('profile');
  const [accountSection, setAccountSection] = useState('profile');
  const [showAccountSettingsModal, setShowAccountSettingsModal] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [showGoalsModal, setShowGoalsModal] = useState(false);
  const [goalsSaving, setGoalsSaving] = useState(false);
  const [goalsList, setGoalsList] = useState([]);
  const [goalDraft, setGoalDraft] = useState({
    title: '',
    target_time: '',
    note: '',
  });
  const [journalModal, setJournalModal] = useState({
    isOpen: false,
    goalId: null,
    what_helped: '',
    hardest_part: '',
    what_next: '',
  });
  const [focusedEventCardId, setFocusedEventCardId] = useState(null);
  const eventCardsRef = useRef({});

  const loadDashboardData = useCallback(async () => {
    try {
      const data = await authAPI.getDashboard();
      setDashboardData(data);
      initializeEditForm(data);
      await loadOrders();
      await loadGroupTrainings();
      await loadEventsForNotifications();
      await loadUserDocuments();
    } catch (error) {
      console.error('Error loading dashboard:', error);
      toast.error('Ошибка загрузки данных');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  useEffect(() => {
    if (dashboardData?.needs_registration_form) {
      setShowRegistrationForm(true);
      setShowHealthForm(false);
      return;
    }
    if (dashboardData?.needs_health_form) {
      setShowHealthForm(true);
    }
  }, [dashboardData]);

  useEffect(() => {
    const rawGoals = String(dashboardData?.health_form?.sport_goals || '');
    setGoalsList(parseGoals(rawGoals));
  }, [dashboardData?.health_form?.sport_goals]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    const canOpenAdmin = Boolean(user?.is_staff || user?.is_superuser);

    if (tab === 'admin' && canOpenAdmin) {
      setActiveTab('admin');
      return;
    }

    // Для /dashboard без query и любых невалидных значений всегда показываем профиль.
    setActiveTab('profile');
  }, [location.search, user?.is_staff, user?.is_superuser]);

  const todayWeekday = getTodayWeekdayRu();

  useEffect(() => {
    if (!groupTrainings.length) {
      setSelectedTrainingDay(todayWeekday);
      return;
    }
    const todayTrainings = groupTrainings.filter((item) => item.weekday === todayWeekday);
    if (todayTrainings.length > 0) {
      setSelectedTrainingDay(todayWeekday);
      return;
    }

    const firstAvailableDay = weekDaysOrder.find((day) =>
      groupTrainings.some((item) => item.weekday === day),
    );
    setSelectedTrainingDay(firstAvailableDay || todayWeekday);
  }, [groupTrainings, todayWeekday]);

  const loadOrders = async () => {
    try {
      setOrdersLoading(true);
      const ordersData = await shopAPI.getOrders();
      const ordersList = Array.isArray(ordersData) ? ordersData : (ordersData?.results || []);
      setOrders(ordersList);
    } catch (error) {
      console.error('Error loading orders:', error);
      // Не показываем ошибку, если просто нет заказов
      setOrders([]);
    } finally {
      setOrdersLoading(false);
    }
  };

  const loadGroupTrainings = async () => {
    try {
      setGroupTrainingsLoading(true);
      const trainingsData = await programsAPI.getGroupTrainings({ latest_week: 1 });
      const trainingsList = Array.isArray(trainingsData) ? trainingsData : (trainingsData?.results || []);
      setGroupTrainings(trainingsList);
    } catch (error) {
      console.error('Error loading group trainings:', error);
      setGroupTrainings([]);
    } finally {
      setGroupTrainingsLoading(false);
    }
  };

  const loadEventsForNotifications = async () => {
    try {
      const eventsData = await eventsAPI.getEvents();
      const eventsList = Array.isArray(eventsData) ? eventsData : (eventsData?.results || []);
      setEventsForNotifications(eventsList);
    } catch (error) {
      console.error('Error loading events for notifications:', error);
      setEventsForNotifications([]);
    }
  };

  const loadUserDocuments = async () => {
    try {
      const docsData = await authAPI.getDocuments();
      const docsList = Array.isArray(docsData) ? docsData : (docsData?.results || []);
      setUserDocuments(docsList);
    } catch (error) {
      console.error('Error loading user documents:', error);
      setUserDocuments([]);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'pending': { text: 'Ожидает обработки', color: 'bg-yellow-100 text-yellow-800' },
      'processing': { text: 'В обработке', color: 'bg-blue-100 text-blue-800' },
      'delivered': { text: 'Доставлен', color: 'bg-purple-100 text-purple-800' },
      'completed': { text: 'Завершен', color: 'bg-green-100 text-green-800' },
      'cancelled': { text: 'Отменен', color: 'bg-red-100 text-red-800' },
    };
    const statusInfo = statusMap[status] || { text: status, color: 'bg-gray-100 text-gray-800' };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusInfo.color}`}>
        {statusInfo.text}
      </span>
    );
  };

  const openOrderDetails = async (order) => {
    try {
      const fullOrder = await shopAPI.getOrder(order.id);
      setSelectedOrder(fullOrder);
    } catch (error) {
      console.error('Error loading order details:', error);
      setSelectedOrder(order);
    }
  };

  const handleRepeatOrder = async (order) => {
    if (!order?.id) return;
    try {
      setRepeatOrderLoadingId(order.id);
      const response = await shopAPI.repeatOrder(order.id);
      const skippedCount = Array.isArray(response?.skipped_items) ? response.skipped_items.length : 0;
      toast.success(
        skippedCount > 0
          ? `Товары добавлены в корзину. Недоступно: ${skippedCount}`
          : 'Товары из заказа добавлены в корзину',
      );
      setSelectedOrder(null);
      setShowAllOrdersModal(false);
      navigate('/shop');
    } catch (error) {
      console.error('Error repeating order:', error);
      toast.error(error?.response?.data?.error || 'Не удалось повторить заказ');
    } finally {
      setRepeatOrderLoadingId(null);
    }
  };

  const initializeEditForm = (data) => {
    if (!data || !data.user || !data.profile) {
      console.error('Invalid dashboard data:', data);
      return;
    }
    
    setEditForm({
      first_name: data.user?.first_name || '',
      last_name: data.user?.last_name || '',
      email: data.user?.email || '',
      phone: data.profile?.phone || '',
      bio: data.profile?.bio || '',
      location: data.profile?.location || '',
      website: data.profile?.website || '',
      birthday: data.profile?.birthday || '',
      social_links: data.profile?.social_links || {},
      email_notifications: data.profile?.email_notifications ?? true,
      push_notifications: data.profile?.push_notifications ?? true,
    });
    if (data.profile?.photo) {
      setPhotoPreview(data.profile.photo);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Проверка типа файла
    if (!file.type.startsWith('image/')) {
      toast.error('Пожалуйста, выберите изображение');
      return;
    }

    // Проверка размера (макс 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Размер файла не должен превышать 5MB');
      return;
    }

    setUploadingPhoto(true);
    try {
      const result = await authAPI.uploadProfilePhoto(file);
      console.log('Photo upload result:', result);
      
      // Обновляем превью сразу после загрузки
      if (result && result.file_url) {
        // Добавляем полный URL если нужно
        const photoUrl = result.file_url.startsWith('http') 
          ? result.file_url 
          : `${window.location.origin}${result.file_url}`;
        setPhotoPreview(photoUrl);
      }
      
      toast.success('Фото профиля обновлено!');
      
      // Перезагружаем данные профиля чтобы получить обновленное фото
      await loadDashboardData();
    } catch (error) {
      console.error('Error uploading photo:', error);
      console.error('Error details:', error.response?.data);
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.detail || 
                          error.response?.data?.message ||
                          error.message || 
                          'Ошибка загрузки фото';
      toast.error(errorMessage);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSaveProfile = async () => {
    const birthdayValue = editForm?.birthday;
    if (birthdayValue) {
      const birthdayDate = new Date(`${birthdayValue}T00:00:00`);
      const today = new Date();

      if (Number.isNaN(birthdayDate.getTime())) {
        toast.error('Некорректная дата рождения');
        return;
      }

      if (birthdayDate > today) {
        toast.error('Дата рождения не может быть в будущем');
        return;
      }

      if (birthdayDate.getFullYear() < 1900) {
        toast.error('Дата рождения должна быть не раньше 1900 года');
        return;
      }
    }

    const socialLinks = editForm?.social_links || {};
    for (const [platform, rawValue] of Object.entries(socialLinks)) {
      const value = String(rawValue || '').trim();
      if (!value) continue;

      let parsedUrl;
      try {
        parsedUrl = new URL(value);
      } catch {
        toast.error(`Введите корректную полную ссылку для ${platform}`);
        return;
      }

      if (!['http:', 'https:'].includes(parsedUrl.protocol)) {
        toast.error(`Ссылка ${platform} должна начинаться с http:// или https://`);
        return;
      }

      const host = parsedUrl.hostname.toLowerCase();
      const allowedHosts = SOCIAL_HOST_RULES[platform];
      if (allowedHosts && !allowedHosts.some((allowed) => host === allowed || host.endsWith(`.${allowed}`))) {
        toast.error(`Ссылка ${platform} должна вести на ${allowedHosts.join(' / ')}`);
        return;
      }
    }

    try {
      const { website, ...payload } = editForm;
      await authAPI.updateProfile(payload);
      toast.success('Профиль обновлен!');
      await loadDashboardData();
    } catch (error) {
      console.error('Error updating profile:', error);
      const apiError = error?.response?.data;
      if (typeof apiError === 'string') {
        toast.error(apiError);
        return;
      }

      const firstFieldError = apiError && typeof apiError === 'object'
        ? Object.values(apiError).flat().find(Boolean)
        : null;
      toast.error(firstFieldError || 'Ошибка обновления профиля');
    }
  };

  const handleHealthFormComplete = async () => {
    await loadDashboardData();
    setShowHealthForm(false);
  };

  const handleRegistrationFormComplete = async () => {
    await loadDashboardData();
    setShowRegistrationForm(false);
  };

  const handleDeleteOwnAccount = async () => {
    const startConfirmed = window.confirm('Удалить ваш аккаунт?');
    if (!startConfirmed) return;

    const deleteAllData = window.confirm(
      'Удалить все ваши данные?\nOK = Да, удалить все\nОтмена = Нет, сохранить данные (анонимизировать аккаунт)'
    );
    const deleteReviews = window.confirm(
      'Удалить ваши отзывы?\nOK = Да, удалить отзывы\nОтмена = Нет, оставить отзывы с вашим именем'
    );

    try {
      setDeletingAccount(true);
      await authAPI.deleteMyAccount({
        delete_all_data: deleteAllData,
        delete_reviews: deleteReviews,
      });
      toast.success('Аккаунт удален');
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Account delete error:', error);
      const detail = error?.response?.data?.detail || error?.response?.data?.error;
      toast.error(detail || 'Не удалось удалить аккаунт');
    } finally {
      setDeletingAccount(false);
    }
  };

  const persistGoals = async (nextGoals, successMessage = 'Goals обновлены') => {
    try {
      setGoalsSaving(true);
      await authAPI.updateHealthForm({ sport_goals: serializeGoals(nextGoals) });
      setGoalsList(nextGoals);
      toast.success(successMessage);
      await loadDashboardData();
    } catch (error) {
      console.error('Error updating goals:', error);
      const apiError = error?.response?.data;
      const firstFieldError = apiError && typeof apiError === 'object'
        ? Object.values(apiError).flat().find(Boolean)
        : null;
      toast.error(firstFieldError || 'Не удалось обновить goals');
      throw error;
    } finally {
      setGoalsSaving(false);
    }
  };

  const handleSaveGoals = async () => {
    if (!goalsList.length) {
      toast.error('Добавьте хотя бы одну цель');
      return;
    }
    await persistGoals(goalsList, 'Goals сохранены');
    setShowGoalsModal(false);
  };

  const handleAddGoal = () => {
    const title = String(goalDraft.title || '').trim();
    const target_time = String(goalDraft.target_time || '').trim();
    const note = String(goalDraft.note || '').trim();

    if (!title) {
      toast.error('Добавьте название цели');
      return;
    }

    const newGoal = {
      id: `goal-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      title,
      target_time,
      note,
      is_done: false,
      completion_journal: null,
      completed_at: null,
    };

    setGoalsList((prev) => [...prev, newGoal]);
    setGoalDraft({ title: '', target_time: '', note: '' });
  };

  const handleRemoveGoal = (goalId) => {
    setGoalsList((prev) => prev.filter((goal) => goal.id !== goalId));
  };

  const openJournalModal = (goal) => {
    setJournalModal({
      isOpen: true,
      goalId: goal.id,
      what_helped: goal.completion_journal?.what_helped || '',
      hardest_part: goal.completion_journal?.hardest_part || '',
      what_next: goal.completion_journal?.what_next || '',
    });
  };

  const closeJournalModal = () => {
    setJournalModal({
      isOpen: false,
      goalId: null,
      what_helped: '',
      hardest_part: '',
      what_next: '',
    });
  };

  const handleToggleGoalDone = (goal) => {
    if (goal.is_done) {
      setGoalsList((prev) =>
        prev.map((item) => (
          item.id === goal.id
            ? { ...item, is_done: false, completed_at: null, completion_journal: null }
            : item
        )),
      );
      return;
    }
    openJournalModal(goal);
  };

  const handleCompleteGoalWithJournal = () => {
    if (!journalModal.goalId) return;

    const journal = {
      what_helped: String(journalModal.what_helped || '').trim(),
      hardest_part: String(journalModal.hardest_part || '').trim(),
      what_next: String(journalModal.what_next || '').trim(),
    };

    setGoalsList((prev) =>
      prev.map((goal) => (
        goal.id === journalModal.goalId
          ? {
            ...goal,
            is_done: true,
            completed_at: new Date().toISOString(),
            completion_journal: journal,
          }
          : goal
      )),
    );
    closeJournalModal();
  };

  const recentOrders = orders.slice(0, 5);
  const groupedTrainings = useMemo(() => {
    const groups = weekDaysOrder.reduce((acc, day) => {
      acc[day] = [];
      return acc;
    }, {});

    groupTrainings.forEach((item) => {
      if (!groups[item.weekday]) {
        groups[item.weekday] = [];
      }
      groups[item.weekday].push(item);
    });

    Object.keys(groups).forEach((day) => {
      groups[day].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    });

    return groups;
  }, [groupTrainings]);

  const selectedDayTrainings = groupedTrainings[selectedTrainingDay] || [];

  const upcomingSubscribedEvents = useMemo(() => {
    const now = new Date();
    return eventsForNotifications
      .filter((event) => event?.is_subscribed && event?.event_date)
      .map((event) => ({ ...event, parsedDate: new Date(event.event_date) }))
      .filter((event) => !Number.isNaN(event.parsedDate.getTime()) && event.parsedDate >= now)
      .sort((a, b) => a.parsedDate.getTime() - b.parsedDate.getTime());
  }, [eventsForNotifications]);

  const healthFormProgress = useMemo(() => {
    const form = dashboardData?.health_form;
    if (!form) {
      return { percent: 0, filled: 0, total: 2 };
    }

    const questionnaireAnswers = form.medical_questionnaire_answers || {};
    const questionnaireAnswered = Object.values(questionnaireAnswers).filter(
      (value) => value === true || value === false,
    ).length;
    const questionnaireDone = questionnaireAnswered === 10;

    const checks = [questionnaireDone, Boolean(form.medical_questionnaire_confirmed)];

    const total = checks.length;
    const filled = checks.filter(Boolean).length;
    const percent = Math.round((filled / total) * 100);
    return { percent, filled, total };
  }, [dashboardData?.health_form]);

  const questionnaireFiles = useMemo(() => {
    const toAbsolute = (url) => {
      if (!url) return null;
      if (url.startsWith('http://') || url.startsWith('https://')) return url;
      if (url.startsWith('/')) return `${window.location.origin}${url}`;
      return url;
    };
    const registration = userDocuments.find((doc) => doc.document_type === 'registration_form_pdf');
    const medical = userDocuments.find((doc) => doc.document_type === 'medical_form_pdf');
    return {
      registration: registration ? { ...registration, file_url: toAbsolute(registration.file_url) } : null,
      medical: medical ? { ...medical, file_url: toAbsolute(medical.file_url) } : null,
    };
  }, [userDocuments]);

  const healthFormPrefillData = useMemo(() => ({
    first_name: dashboardData?.user?.first_name || '',
    last_name: dashboardData?.user?.last_name || '',
    phone_number: dashboardData?.profile?.phone || '',
    date_of_birth: dashboardData?.profile?.birthday || '',
    residence: dashboardData?.profile?.location || '',
    workplace: '',
    gender: dashboardData?.health_form?.gender || '',
  }), [dashboardData]);

  const focusEventCard = useCallback((eventId) => {
    if (!eventId) return;
    setFocusedEventCardId(eventId);
    const card = eventCardsRef.current[eventId];
    if (card?.scrollIntoView) {
      card.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    setTimeout(() => setFocusedEventCardId(null), 1800);
  }, []);

  const toggleGroupTrainingSubscription = async (training) => {
    try {
      setGroupTrainingSubscriptionLoadingId(training.id);
      const updatedTraining = training.is_subscribed
        ? await programsAPI.unsubscribeGroupTraining(training.id)
        : await programsAPI.subscribeGroupTraining(training.id);

      setGroupTrainings((prev) => prev.map((item) => (item.id === training.id ? updatedTraining : item)));
      toast.success(training.is_subscribed ? 'Запись отменена' : 'Вы записаны на тренировку');
    } catch (error) {
      console.error('Error toggling group training subscription:', error);
      toast.error('Не удалось обновить запись на тренировку');
    } finally {
      setGroupTrainingSubscriptionLoadingId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-850 to-gray-900 flex items-center justify-center pt-24">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-white"></div>
      </div>
    );
  }

  if (!dashboardData || !dashboardData.user || !dashboardData.profile) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center pt-24">
        <div className="text-center text-gray-700">
          <h2 className="text-2xl font-bold mb-4">
            {!dashboardData ? 'Загрузка данных...' : 'Ошибка загрузки данных'}
          </h2>
          <Button onClick={loadDashboardData}>Попробовать снова</Button>
        </div>
      </div>
    );
  }

  const { user: dashboardUser, profile } = dashboardData;
  const isAdminUser = Boolean(
    dashboardUser?.is_staff
    || dashboardUser?.is_superuser
    || user?.is_staff
    || user?.is_superuser
  );
  const athleteName = [
    dashboardUser?.first_name,
    dashboardUser?.last_name,
  ]
    .map((value) => String(value || '').trim())
    .filter(Boolean)
    .join(' ') || dashboardUser?.username || user?.username || 'Пользователь';

  return (
    <div className="min-h-screen bg-slate-100 pt-20 sm:pt-24 pb-8 sm:pb-12">
      <div className="w-full px-3 sm:px-4 md:px-6 lg:px-8 xl:px-10">
        {activeTab === 'admin' && isAdminUser && (
          <div className="max-w-[1360px] mx-auto">
            <AdminDashboardTab />
          </div>
        )}

        <div className={activeTab === 'profile' ? '' : 'hidden'}>
        <div className="grid xl:grid-cols-[280px_minmax(0,760px)_320px] xl:justify-center gap-4 sm:gap-6 lg:gap-8">
          {/* Left Column - Profile Side */}
          <div className="space-y-4 sm:space-y-6">
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
              <div className="bg-gradient-to-r from-[#662d91]/10 via-[#4897d2]/10 to-[#28c4f4]/10 px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="relative group">
                      <div className="w-14 h-14 rounded-full bg-gray-100 border border-gray-200 overflow-hidden">
                        {photoPreview ? (
                          <img
                            src={photoPreview}
                            alt={athleteName}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <User className="w-7 h-7 text-[#4897d2]" />
                          </div>
                        )}
                      </div>
                      <label className="absolute inset-0 bg-black/45 rounded-full flex items-center justify-center cursor-pointer opacity-0 group-hover:opacity-100 active:opacity-100 transition-opacity">
                        <Camera className="w-4 h-4 text-white" />
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handlePhotoUpload}
                          disabled={uploadingPhoto}
                          className="hidden"
                        />
                      </label>
                      {uploadingPhoto && (
                        <div className="absolute inset-0 bg-black/45 rounded-full flex items-center justify-center z-10">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        </div>
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs uppercase tracking-[0.18em] text-gray-500">Профиль</p>
                      <h2 className="text-2xl font-bold text-gray-900 leading-tight truncate">{athleteName}</h2>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setAccountSection('profile');
                      setShowAccountSettingsModal(true);
                    }}
                    className="shrink-0 p-2.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-white/80 hover:text-[#2f77b2] transition-colors"
                    aria-label="Редактировать профиль"
                    title="Редактировать профиль"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="p-4 sm:p-6 space-y-3 sm:space-y-4">
                <div className="border border-gray-200 rounded-lg sm:rounded-xl p-3 sm:p-4 bg-gray-50/60 space-y-2 text-sm text-gray-700">
                  {dashboardUser?.email && (
                    <p className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-gray-500" />
                      <span className="truncate">{dashboardUser.email}</span>
                    </p>
                  )}
                  {profile?.phone && (
                    <p className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-gray-500" />
                      {profile.phone}
                    </p>
                  )}
                  {profile?.location && (
                    <p className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-gray-500" />
                      {profile.location}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
              <div className="bg-gradient-to-r from-[#662d91]/10 via-[#4897d2]/10 to-[#28c4f4]/10 px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
                <h2 className="text-lg sm:text-xl font-bold text-gray-900">Анкета атлета</h2>
              </div>
              <div className="p-4 sm:p-6 space-y-3">
                {dashboardData?.needs_health_form ? (
                  <>
                    <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                      В анкете не хватает обязательных данных
                      {Array.isArray(dashboardData?.health_form_required_missing)
                        ? ` (${dashboardData.health_form_required_missing.length}).`
                        : '.'} Заполните недостающие поля.
                    </p>
                    <div className="rounded-lg border border-gray-200 p-3 bg-gray-50">
                      <div className="flex items-center justify-between text-sm mb-2">
                        <span className="text-gray-700">Заполненность анкеты</span>
                        <span className="font-semibold text-gray-900">{healthFormProgress.percent}%</span>
                      </div>
                      <div className="h-2 rounded-full bg-gray-200 overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-[#662d91] via-[#4897d2] to-[#28c4f4]"
                          style={{ width: `${healthFormProgress.percent}%` }}
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        Заполнено {healthFormProgress.filled} из {healthFormProgress.total} ключевых полей
                      </p>
                    </div>
                  </>
                ) : null}
                <div className="grid grid-cols-1 gap-2">
                  <Button
                    onClick={() => {
                      setShowRegistrationForm(true);
                    }}
                    className="w-full text-sm py-2.5"
                  >
                    Анкета атлета
                  </Button>
                  <Button
                    onClick={() => {
                      setAccountSection('health');
                      setShowAccountSettingsModal(true);
                      setShowHealthForm(true);
                    }}
                    className="w-full text-sm py-2.5"
                  >
                    Анкета медицинская
                  </Button>
                </div>
                <div className="rounded-lg border border-gray-200 p-3 bg-gray-50 space-y-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-600">Файлы анкет</p>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-700">Анкета регистрации PDF</span>
                    {questionnaireFiles.registration?.file_url ? (
                      <a href={questionnaireFiles.registration.file_url} target="_blank" rel="noreferrer" className="text-[#4897d2] font-semibold hover:underline">
                        Скачать
                      </a>
                    ) : <span className="text-gray-400">—</span>}
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-700">Медицинская анкета PDF</span>
                    {questionnaireFiles.medical?.file_url ? (
                      <a href={questionnaireFiles.medical.file_url} target="_blank" rel="noreferrer" className="text-[#4897d2] font-semibold hover:underline">
                        Скачать
                      </a>
                    ) : <span className="text-gray-400">—</span>}
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
              <div className="bg-gradient-to-r from-[#662d91]/10 via-[#4897d2]/10 to-[#28c4f4]/10 px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
                <h2 className="text-lg sm:text-xl font-bold text-gray-900 flex items-center gap-2">
                  <Flag className="w-4 h-4 sm:w-5 sm:h-5 text-[#4897d2]" />
                  Events
                </h2>
              </div>
              <div className="p-4 sm:p-6 space-y-3">
                {upcomingSubscribedEvents.length > 0 ? (
                  upcomingSubscribedEvents.map((event) => (
                    <button
                      key={event.id}
                      type="button"
                      ref={(node) => {
                        if (node) eventCardsRef.current[event.id] = node;
                      }}
                      onClick={() => focusEventCard(event.id)}
                      className={`w-full text-left rounded-lg border px-3 py-2.5 transition ${
                        focusedEventCardId === event.id
                          ? 'border-[#4897d2] bg-[#4897d2]/10'
                          : 'border-gray-200 bg-gray-50 hover:bg-gray-100'
                      }`}
                    >
                      <p className="text-xs uppercase tracking-wide text-[#662d91]">
                        {event.parsedDate.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })}
                      </p>
                      <p className="text-sm font-semibold text-gray-900 mt-1">{event.title}</p>
                    </button>
                  ))
                ) : (
                  <p className="text-sm text-gray-600">
                    Пока нет будущих забегов, куда вы записаны.
                  </p>
                )}
              </div>
            </div>

            <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
              <div className="bg-gradient-to-r from-[#662d91]/10 via-[#4897d2]/10 to-[#28c4f4]/10 px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
                <h2 className="text-lg sm:text-xl font-bold text-gray-900 flex items-center gap-2">
                  <Target className="w-4 h-4 sm:w-5 sm:h-5 text-[#4897d2]" />
                  Goals
                </h2>
              </div>
              <div className="p-4 sm:p-6 space-y-3">
                {goalsList.length > 0 ? (
                  goalsList.map((goal) => (
                    <div key={goal.id} className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5">
                      <button
                        type="button"
                        onClick={() => handleToggleGoalDone(goal)}
                        className="w-full flex items-start gap-2 text-left"
                      >
                        {goal.is_done ? (
                          <CheckSquare className="w-4 h-4 mt-0.5 text-green-600 shrink-0" />
                        ) : (
                          <Square className="w-4 h-4 mt-0.5 text-gray-400 shrink-0" />
                        )}
                        <div className="min-w-0">
                          <p className={`text-sm font-medium ${goal.is_done ? 'line-through text-gray-500' : 'text-gray-900'}`}>
                            {goal.title}
                          </p>
                          {goal.target_time && (
                            <p className="text-xs text-gray-500 mt-0.5">Целевое время: {goal.target_time}</p>
                          )}
                          {goal.is_done && (
                            <p className="text-xs text-green-700 mt-1">Done</p>
                          )}
                        </div>
                      </button>
                      {goal.is_done && goal.completion_journal?.what_helped && (
                        <p className="text-xs text-gray-600 mt-2 pl-6">
                          Дневник: {goal.completion_journal.what_helped}
                        </p>
                      )}
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-600">
                    Добавьте цель в формате todo и отмечайте как Done по мере выполнения.
                  </p>
                )}
                <Button
                  onClick={() => setShowGoalsModal(true)}
                  className="w-full text-sm py-2.5"
                >
                  Управлять goals
                </Button>
              </div>
            </div>

            <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
              <div className="bg-gradient-to-r from-[#662d91]/10 via-[#4897d2]/10 to-[#28c4f4]/10 px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
                <h2 className="text-lg sm:text-xl font-bold text-gray-900 flex items-center gap-2">
                  <Link2 className="w-4 h-4 sm:w-5 sm:h-5 text-[#4897d2]" />
                  Интеграции
                </h2>
              </div>
              <div className="p-4 sm:p-6 space-y-2">
                <p className="text-sm text-gray-700">Следующий шаг: подключим часы и Strava, чтобы подтягивать больше метрик.</p>
                <p className="text-xs text-gray-500">Garmin • Coros • Polar • Strava</p>
              </div>
            </div>
          </div>

          {/* Center Column - Main Content */}
          <div className="space-y-4 sm:space-y-5 xl:max-w-[760px] w-full">
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-3 sm:px-4 py-3 border-b border-gray-200 bg-white">
                <div className="flex items-center justify-between gap-3">
                  <h2 className="text-base sm:text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-[#4897d2]" />
                    Тренировочная лента
                  </h2>
                </div>
              </div>
              <div className="p-3 sm:p-4 bg-slate-50">
                {groupTrainingsLoading ? (
                  <div className="flex justify-center py-10">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4897d2]"></div>
                  </div>
                ) : groupTrainings.length > 0 ? (
                  <div className="space-y-3">
                    <div className="text-xs sm:text-sm text-gray-500 px-1">Выберите день недели</div>

                    <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-7 gap-1.5">
                      {weekDaysOrder.map((day) => {
                        const count = (groupedTrainings[day] || []).length;
                        const isActive = selectedTrainingDay === day;
                        const isToday = todayWeekday === day;
                        return (
                          <button
                            key={day}
                            type="button"
                            onClick={() => setSelectedTrainingDay(day)}
                            className={`text-left rounded-md border px-2.5 py-2 transition-colors ${
                              isActive
                                ? 'border-[#4897d2] bg-[#4897d2]/12'
                                : 'border-gray-200 bg-white hover:bg-gray-50'
                            } ${isToday ? 'ring-1 ring-[#662d91]/40' : ''}`}
                          >
                            <p className="text-xs font-semibold text-gray-900">{weekDaysShort[day] || day}</p>
                            <p className="text-[11px] text-gray-500">{count} трен.{isToday ? ' • сегодня' : ''}</p>
                          </button>
                        );
                      })}
                    </div>

                    <div className="space-y-3">
                      <div className="text-sm font-medium text-gray-800 px-1">
                        {selectedTrainingDay === todayWeekday ? 'Тренировки на сегодня' : `Тренировки: ${selectedTrainingDay}`}
                      </div>
                      {selectedDayTrainings.length > 0 ? (
                        selectedDayTrainings.map((training) => (
                          <article
                            key={training.id}
                            className="border border-gray-200 rounded-lg p-3.5 sm:p-4 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04)] transition"
                          >
                            <p className="text-sm sm:text-[15px] font-semibold text-gray-900 leading-relaxed">
                              {training.is_new ? '🆕 ' : ''}
                              {training.icon ? `${training.icon} ` : ''}
                              {training.weekday}, {training.time}, {training.title}
                            </p>
                            {training.location && (
                              <p className="text-sm text-gray-700 mt-1.5">{training.location}</p>
                            )}
                            {training.details && (
                              <p className="text-sm text-gray-700 mt-1.5">{training.details}</p>
                            )}
                            {training.note && (
                              <p className="text-sm text-gray-600 mt-1.5 italic">{training.note}</p>
                            )}
                            <div className="mt-3 p-2.5 rounded-lg bg-gray-50 border border-gray-200">
                              <div className="flex items-center justify-between gap-2">
                                <p className="text-xs sm:text-sm text-gray-700 inline-flex items-center gap-1">
                                  <Users className="w-4 h-4" />
                                  Записались: <span className="font-semibold">{training.participants_count || 0}</span>
                                </p>
                                <button
                                  type="button"
                                  disabled={groupTrainingSubscriptionLoadingId === training.id}
                                  onClick={() => toggleGroupTrainingSubscription(training)}
                                  className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium ${
                                    training.is_subscribed
                                      ? 'bg-green-100 text-green-800 hover:bg-green-200'
                                      : 'bg-[#4897d2] text-white hover:opacity-90'
                                  } disabled:opacity-60`}
                                >
                                  {training.is_subscribed ? <UserCheck className="w-4 h-4" /> : <UserPlus className="w-4 h-4" />}
                                  {groupTrainingSubscriptionLoadingId === training.id
                                    ? 'Обработка...'
                                    : training.is_subscribed ? 'Вы записаны' : 'Записаться'}
                                </button>
                              </div>
                              <button
                                type="button"
                                onClick={() => setParticipantsModalData({
                                  isOpen: true,
                                  title: `Участники: ${training.weekday}, ${training.time}`,
                                  participants: training.participants_preview || [],
                                  totalCount: training.participants_count || 0,
                                })}
                                className="text-xs text-[#4897d2] hover:underline mt-2 ml-1"
                              >
                                Участники
                              </button>
                            </div>
                            {training.meeting_point_url && (
                              <p className="text-sm mt-2.5">
                                <span className="text-gray-700">{training.meeting_point_label || 'Точка сбора'} 👉🏻 </span>
                                <a
                                  href={training.meeting_point_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-[#4897d2] hover:underline break-all"
                                >
                                  {training.meeting_point_url}
                                </a>
                              </p>
                            )}
                          </article>
                        ))
                      ) : (
                        <div className="border border-dashed border-gray-300 rounded-lg p-5 text-sm text-gray-500 bg-white">
                          На выбранный день тренировок нет.
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-10 sm:py-16">
                    <Calendar className="w-12 h-12 sm:w-16 sm:h-16 text-gray-300 mx-auto mb-3 sm:mb-4" />
                    <p className="text-sm sm:text-base text-gray-700 font-medium">Пока нет групповых тренировок</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-4 sm:space-y-6">
            {/* Recent Orders Section */}
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
              <div className="bg-gradient-to-r from-[#662d91]/10 via-[#4897d2]/10 to-[#28c4f4]/10 px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
                <h2 className="text-lg sm:text-xl font-bold text-gray-900 flex items-center gap-2">
                  <ShoppingBag className="w-4 h-4 sm:w-5 sm:h-5 text-[#4897d2]" />
                  Последние заказы
                </h2>
              </div>
              <div className="p-4 sm:p-6">
                {ordersLoading ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4897d2]"></div>
                  </div>
                ) : orders.length > 0 ? (
                  <div className="space-y-3">
                    {recentOrders.map((order) => (
                      <div key={order.id} className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50 transition-colors">
                        <div className="flex items-center justify-between gap-3">
                          <button
                            onClick={() => openOrderDetails(order)}
                            className="text-sm font-semibold text-gray-900 hover:text-[#4897d2] transition-colors"
                          >
                            Заказ #{order.id}
                          </button>
                          {getStatusBadge(order.status)}
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                          {new Date(order.created_at).toLocaleDateString('ru-RU', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </p>
                      </div>
                    ))}
                    <button
                      onClick={() => setShowAllOrdersModal(true)}
                      className="w-full mt-2 px-4 py-2 text-sm bg-gradient-to-r from-[#662d91] via-[#4897d2] to-[#28c4f4] text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
                    >
                      Смотреть все заказы
                    </button>
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <Package className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm text-gray-600 mb-3">У вас пока нет заказов</p>
                    <button
                      onClick={() => navigate('/shop')}
                      className="px-4 py-2 text-sm bg-gradient-to-r from-[#662d91] via-[#4897d2] to-[#28c4f4] text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
                    >
                      Перейти в магазин
                    </button>
                  </div>
                )}
              </div>
            </div>

          </div>
        </div>

        {showAccountSettingsModal && (
          <div
            className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-3 sm:p-6"
            onClick={() => setShowAccountSettingsModal(false)}
          >
            <div
              className="bg-white rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-5xl max-h-[92vh] overflow-hidden mt-6 sm:mt-8"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between z-10">
                <h2 className="text-lg sm:text-xl font-bold text-gray-900">Account Settings</h2>
                <button
                  type="button"
                  onClick={() => setShowAccountSettingsModal(false)}
                  className="p-1.5 rounded-md text-gray-500 hover:text-gray-800 hover:bg-gray-100"
                  aria-label="Закрыть"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="grid md:grid-cols-[220px_minmax(0,1fr)] max-h-[calc(92vh-68px)]">
                <aside className="border-r border-gray-200 bg-gray-50 overflow-y-auto">
                  <div className="p-4">
                    <p className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-3">Account</p>
                    <div className="space-y-1">
                      <button
                        type="button"
                        onClick={() => setAccountSection('profile')}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium ${accountSection === 'profile' ? 'bg-[#4897d2] text-white' : 'text-gray-700 hover:bg-gray-100'}`}
                      >
                        Profile
                      </button>
                      <button
                        type="button"
                        onClick={() => setAccountSection('health')}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium ${accountSection === 'health' ? 'bg-[#4897d2] text-white' : 'text-gray-700 hover:bg-gray-100'}`}
                      >
                        Анкета о здоровье
                      </button>
                      <button
                        type="button"
                        onClick={() => setAccountSection('security')}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium ${accountSection === 'security' ? 'bg-[#4897d2] text-white' : 'text-gray-700 hover:bg-gray-100'}`}
                      >
                        Безопасность
                      </button>
                    </div>
                  </div>
                </aside>

                <div className="p-4 sm:p-6 overflow-y-auto">
                  {accountSection === 'profile' ? (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between gap-3 pb-3 border-b border-gray-200">
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900">Profile</h3>
                        <div className="relative group">
                          <div className="w-20 h-20 rounded-lg bg-gray-100 border border-gray-200 overflow-hidden">
                            {photoPreview ? (
                              <img src={photoPreview} alt={athleteName} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <User className="w-7 h-7 text-[#4897d2]" />
                              </div>
                            )}
                          </div>
                          <label className="absolute inset-0 bg-black/45 rounded-lg flex items-center justify-center cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity">
                            <Camera className="w-4 h-4 text-white" />
                            <input type="file" accept="image/*" onChange={handlePhotoUpload} disabled={uploadingPhoto} className="hidden" />
                          </label>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                        <div>
                          <label className="text-xs sm:text-sm font-medium text-gray-500 mb-1 block">Имя</label>
                          <input
                            type="text"
                            value={editForm.first_name}
                            onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
                            className="w-full px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4897d2] focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="text-xs sm:text-sm font-medium text-gray-500 mb-1 block">Фамилия</label>
                          <input
                            type="text"
                            value={editForm.last_name}
                            onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
                            className="w-full px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4897d2] focus:border-transparent"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                        <div>
                          <label className="text-xs sm:text-sm font-medium text-gray-500 mb-1 block">Дата рождения</label>
                          <input
                            type="date"
                            value={editForm.birthday || ''}
                            onChange={(e) => setEditForm({ ...editForm, birthday: e.target.value })}
                            className="w-full px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4897d2] focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="text-xs sm:text-sm font-medium text-gray-500 mb-1 block">Телефон</label>
                          <input
                            type="tel"
                            value={editForm.phone || ''}
                            onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                            className="w-full px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4897d2] focus:border-transparent"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="text-xs sm:text-sm font-medium text-gray-500 mb-1 block">Локация</label>
                        <input
                          type="text"
                          value={editForm.location || ''}
                          onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
                          className="w-full px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4897d2] focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label className="text-xs sm:text-sm font-medium text-gray-500 mb-1 block">О себе</label>
                        <textarea
                          value={editForm.bio || ''}
                          onChange={(e) => setEditForm({ ...editForm, bio: e.target.value })}
                          rows="3"
                          className="w-full px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4897d2] focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label className="text-xs sm:text-sm font-medium text-gray-500 mb-2 block">Социальные сети (полные ссылки)</label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                          {['instagram', 'facebook', 'twitter', 'linkedin'].map((social) => (
                            <input
                              key={social}
                              type="text"
                              value={editForm.social_links[social] || ''}
                              onChange={(e) => setEditForm({
                                ...editForm,
                                social_links: { ...editForm.social_links, [social]: e.target.value }
                              })}
                              placeholder={
                                social === 'instagram'
                                  ? 'https://instagram.com/username'
                                  : social === 'facebook'
                                    ? 'https://facebook.com/username'
                                    : social === 'twitter'
                                      ? 'https://x.com/username'
                                      : 'https://linkedin.com/in/username'
                              }
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4897d2] focus:border-transparent"
                            />
                          ))}
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-2 pt-2">
                        <Button onClick={handleSaveProfile} className="text-sm py-2.5">Сохранить</Button>
                        <button
                          type="button"
                          onClick={() => initializeEditForm(dashboardData)}
                          className="px-4 py-2.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm"
                        >
                          Отмена
                        </button>
                      </div>
                    </div>
                  ) : accountSection === 'health' ? (
                    <div className="space-y-4">
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900">Анкета о здоровье</h3>
                      {dashboardData?.needs_health_form ? (
                        <>
                          <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                            Не хватает обязательных полей
                            {Array.isArray(dashboardData?.health_form_required_missing)
                              ? ` (${dashboardData.health_form_required_missing.length}).`
                              : '.'}
                          </p>
                          <div className="rounded-lg border border-gray-200 p-3 bg-gray-50">
                            <div className="flex items-center justify-between text-sm mb-2">
                              <span className="text-gray-700">Заполненность анкеты</span>
                              <span className="font-semibold text-gray-900">{healthFormProgress.percent}%</span>
                            </div>
                            <div className="h-2 rounded-full bg-gray-200 overflow-hidden">
                              <div
                                className="h-full bg-gradient-to-r from-[#662d91] via-[#4897d2] to-[#28c4f4]"
                                style={{ width: `${healthFormProgress.percent}%` }}
                              />
                            </div>
                          </div>
                        </>
                      ) : null}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <Button
                          onClick={() => setShowRegistrationForm(true)}
                          className="text-sm py-2.5"
                        >
                          Анкета атлета
                        </Button>
                        <Button onClick={() => setShowHealthForm(true)} className="text-sm py-2.5">
                          Анкета медицинская
                        </Button>
                      </div>
                      <div className="rounded-lg border border-gray-200 p-3 bg-gray-50 space-y-2">
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-600">Файлы анкет</p>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-700">Анкета регистрации PDF</span>
                          {questionnaireFiles.registration?.file_url ? (
                            <a href={questionnaireFiles.registration.file_url} target="_blank" rel="noreferrer" className="text-[#4897d2] font-semibold hover:underline">
                              Скачать
                            </a>
                          ) : <span className="text-gray-400">—</span>}
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-700">Медицинская анкета PDF</span>
                          {questionnaireFiles.medical?.file_url ? (
                            <a href={questionnaireFiles.medical.file_url} target="_blank" rel="noreferrer" className="text-[#4897d2] font-semibold hover:underline">
                              Скачать
                            </a>
                          ) : <span className="text-gray-400">—</span>}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900">Удаление аккаунта</h3>
                      <p className="text-sm text-gray-600">
                        Можно удалить аккаунт полностью или анонимизировать его, а также выбрать что делать с отзывами.
                      </p>
                      <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                        <p className="text-sm text-red-700">
                          Это действие может быть необратимым.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={handleDeleteOwnAccount}
                        disabled={deletingAccount}
                        className="px-4 py-2.5 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed text-sm"
                      >
                        {deletingAccount ? 'Удаляем...' : 'Удалить аккаунт'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* All Orders Modal */}
        {showAllOrdersModal && (
          <div
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-3 sm:p-4"
            onClick={() => setShowAllOrdersModal(false)}
          >
            <div
              className="bg-white rounded-xl sm:rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-white border-b border-gray-200 p-4 sm:p-6 flex items-center justify-between z-10">
                <h3 className="text-lg sm:text-2xl font-bold text-gray-900">Все заказы</h3>
                <button
                  onClick={() => setShowAllOrdersModal(false)}
                  className="text-sm sm:text-base text-gray-600 hover:text-gray-900"
                >
                  Закрыть
                </button>
              </div>
              <div className="p-4 sm:p-6 space-y-3">
                {orders.map((order) => (
                  <div key={order.id} className="border border-gray-200 rounded-xl p-4 bg-gray-50">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-semibold text-gray-900">Заказ #{order.id}</p>
                        <p className="text-sm text-gray-500">
                          {new Date(order.created_at).toLocaleDateString('ru-RU', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                        <p className="text-sm text-gray-600 mt-1">
                          Товаров: {order.total_items || order.items?.length || 0} • Сумма: {parseFloat(order.total_price || 0).toFixed(2)}₪
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {getStatusBadge(order.status)}
                        <button
                          onClick={() => {
                            setShowAllOrdersModal(false);
                            openOrderDetails(order);
                          }}
                          className="px-3 py-2 text-sm rounded-lg border border-gray-300 hover:bg-white transition-colors"
                        >
                          Подробнее
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Order Details Modal */}
        {selectedOrder && (
          <OrderDetailsModal
            order={selectedOrder}
            onClose={() => setSelectedOrder(null)}
            onRepeat={() => handleRepeatOrder(selectedOrder)}
            repeatLoading={repeatOrderLoadingId === selectedOrder.id}
          />
        )}

        {showGoalsModal && (
          <div
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowGoalsModal(false)}
          >
            <div
              className="bg-white rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-4 sm:p-6 border-b border-gray-200">
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 flex items-center gap-2">
                  <ClipboardList className="w-5 h-5 text-[#4897d2]" />
                  Goals as Todo
                </h3>
                <p className="text-sm text-gray-600 mt-1">Добавляйте цели, отмечайте `Done` и заполняйте мини-дневник достижения</p>
              </div>
              <div className="p-4 sm:p-6 space-y-4 max-h-[70vh] overflow-y-auto">
                <div className="rounded-xl border border-gray-200 p-3 sm:p-4 bg-gray-50 space-y-2">
                  <input
                    type="text"
                    value={goalDraft.title}
                    onChange={(e) => setGoalDraft((prev) => ({ ...prev, title: e.target.value }))}
                    placeholder="Новая цель (например, 10 км без остановки)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4897d2] focus:border-transparent"
                  />
                  <input
                    type="text"
                    value={goalDraft.target_time}
                    onChange={(e) => setGoalDraft((prev) => ({ ...prev, target_time: e.target.value }))}
                    placeholder="Целевое время (опционально)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4897d2] focus:border-transparent"
                  />
                  <textarea
                    value={goalDraft.note}
                    onChange={(e) => setGoalDraft((prev) => ({ ...prev, note: e.target.value }))}
                    placeholder="Комментарий (опционально)"
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4897d2] focus:border-transparent"
                  />
                  <Button type="button" onClick={handleAddGoal} className="text-sm">
                    Добавить goal
                  </Button>
                </div>

                <div className="space-y-2">
                  {goalsList.length === 0 ? (
                    <p className="text-sm text-gray-600">
                      Пока нет goals. Добавьте первую цель выше.
                    </p>
                  ) : (
                    goalsList.map((goal) => (
                      <div key={goal.id} className="rounded-lg border border-gray-200 p-3 sm:p-4 bg-white">
                        <div className="flex items-start justify-between gap-2">
                          <button
                            type="button"
                            onClick={() => handleToggleGoalDone(goal)}
                            className="flex items-start gap-2 text-left"
                          >
                            {goal.is_done ? (
                              <CheckSquare className="w-5 h-5 mt-0.5 text-green-600 shrink-0" />
                            ) : (
                              <Square className="w-5 h-5 mt-0.5 text-gray-400 shrink-0" />
                            )}
                            <div>
                              <p className={`text-sm font-semibold ${goal.is_done ? 'line-through text-gray-500' : 'text-gray-900'}`}>
                                {goal.title}
                              </p>
                              {goal.target_time && (
                                <p className="text-xs text-gray-500 mt-0.5">Целевое время: {goal.target_time}</p>
                              )}
                              {goal.note && (
                                <p className="text-xs text-gray-600 mt-1">{goal.note}</p>
                              )}
                              {goal.is_done && goal.completed_at && (
                                <p className="text-xs text-green-700 mt-1">
                                  Done: {new Date(goal.completed_at).toLocaleDateString('ru-RU')}
                                </p>
                              )}
                            </div>
                          </button>
                          <div className="flex items-center gap-1">
                            {goal.is_done && (
                              <button
                                type="button"
                                onClick={() => openJournalModal(goal)}
                                className="px-2 py-1 text-xs rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
                              >
                                Дневник
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => handleRemoveGoal(goal.id)}
                              className="p-1.5 rounded-md border border-gray-300 text-gray-500 hover:bg-red-50 hover:text-red-600"
                              aria-label="Удалить goal"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
              <div className="p-4 sm:p-6 border-t border-gray-200 flex gap-2">
                <Button onClick={handleSaveGoals} className="text-sm" disabled={goalsSaving}>
                  {goalsSaving ? 'Сохраняем...' : 'Сохранить todo-goals'}
                </Button>
                <button
                  type="button"
                  onClick={() => setShowGoalsModal(false)}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm"
                >
                  Отмена
                </button>
              </div>
            </div>
          </div>
        )}

        {journalModal.isOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4"
            onClick={closeJournalModal}
          >
            <div
              className="bg-white rounded-xl shadow-2xl w-full max-w-lg"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-4 sm:p-6 border-b border-gray-200">
                <h3 className="text-lg font-bold text-gray-900">Мини-дневник достижения</h3>
                <p className="text-sm text-gray-600 mt-1">Ответьте коротко, как вы достигли цели</p>
              </div>
              <div className="p-4 sm:p-6 space-y-3">
                <textarea
                  value={journalModal.what_helped}
                  onChange={(e) => setJournalModal((prev) => ({ ...prev, what_helped: e.target.value }))}
                  placeholder="Что помогло достичь цель?"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4897d2] focus:border-transparent"
                />
                <textarea
                  value={journalModal.hardest_part}
                  onChange={(e) => setJournalModal((prev) => ({ ...prev, hardest_part: e.target.value }))}
                  placeholder="Что было самым сложным?"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4897d2] focus:border-transparent"
                />
                <textarea
                  value={journalModal.what_next}
                  onChange={(e) => setJournalModal((prev) => ({ ...prev, what_next: e.target.value }))}
                  placeholder="Какую цель ставите следующей?"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4897d2] focus:border-transparent"
                />
              </div>
              <div className="p-4 sm:p-6 border-t border-gray-200 flex gap-2">
                <Button type="button" onClick={handleCompleteGoalWithJournal} className="text-sm">
                  Отметить как Done
                </Button>
                <button
                  type="button"
                  onClick={closeJournalModal}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm"
                >
                  Отмена
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Registration Form Modal */}
        {showRegistrationForm && (
          <RegistrationFormModal
            isOpen={showRegistrationForm}
            onClose={() => {
              if (dashboardData?.needs_registration_form) return;
              setShowRegistrationForm(false);
            }}
            onComplete={handleRegistrationFormComplete}
            initialData={dashboardData?.health_form}
            prefillData={healthFormPrefillData}
            required={Boolean(dashboardData?.needs_registration_form)}
          />
        )}

        {/* Health Form Modal */}
        {showHealthForm && (
          <HealthFormModal
            isOpen={showHealthForm}
            onClose={() => {
              if (dashboardData?.needs_health_form || dashboardData?.needs_registration_form) return;
              setShowHealthForm(false);
            }}
            onComplete={handleHealthFormComplete}
            initialData={dashboardData?.health_form}
            prefillData={healthFormPrefillData}
            required={Boolean(dashboardData?.needs_health_form)}
          />
        )}

        <ParticipantsModal
          isOpen={participantsModalData.isOpen}
          onClose={() => setParticipantsModalData({ isOpen: false, title: '', participants: [], totalCount: 0 })}
          title={participantsModalData.title}
          participants={participantsModalData.participants}
          totalCount={participantsModalData.totalCount}
        />
        </div>
      </div>
    </div>
  );
};

export default UserDashboard;
